import { useLingui } from "@lingui/react";
import type { NotificationEvent } from "@/server-types";
import { cn } from "@/utils";
import { useShowsMarketplaceLinks } from "../hooks/useShowsMarketplaceLinks";
import type { ChannelKey } from "../types";
import {
  mergeTagGroupsForEvent,
  worstCaseLengthForTag,
  type CharUsageSource,
} from "../utils/mergeTags";

type MergeTagChipRowProps = {
  event: NotificationEvent;
  onInsert: (tag: string) => void;
  className?: string;
  /**
   * Tenant data used to compute the per-tag worst-case length shown on
   * each chip (DEV-8044). The same source feeds the total-character
   * counter (`expandForCharUsage`), so the hint and the budget never
   * disagree. Optional — chips render without lengths when omitted.
   */
  charUsageSource?: CharUsageSource;
  /**
   * Placeholders (e.g. `{rescheduleFromDateTime}`) to omit from the row.
   * The event's tag catalog is send-pipeline-scoped, so a caller whose
   * context can't populate one of the tags hides it here instead of the
   * catalog growing per-caller variants (DEV-8727).
   */
  excludeTags?: ReadonlyArray<string>;
  /** The channel being edited; channel-bound tags such as the billing link need it. */
  channel?: ChannelKey;
};

// Insertion-target-agnostic: the parent passes an `onInsert` callback that
// knows how to route the tag into whatever is focused — a plain textarea,
// an `<input>` (email subject), or the TipTap `MarkdownEditor`. The chip
// row renders one button per allowed merge tag for the event (the
// allowlist comes from the server's `getTagsForEvent`, which already
// filters event-scoped tags like `rescheduleFromDateTime`).
export function MergeTagChipRow({
  event,
  onInsert,
  className,
  charUsageSource,
  excludeTags,
  channel,
}: MergeTagChipRowProps) {
  const { i18n } = useLingui();
  const showsMarketplaceLinks = useShowsMarketplaceLinks();
  const groups = mergeTagGroupsForEvent(event, {
    showsMarketplaceLinks,
    channel,
  });
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {groups.map((group) =>
        group.items
          .filter((item) => !excludeTags?.includes(item.tag))
          .map((item) => {
            const length = charUsageSource
              ? worstCaseLengthForTag(item.key, charUsageSource)
              : null;
            return (
              <button
                key={item.tag}
                type="button"
                onClick={() => onInsert(item.tag)}
                title={item.tag}
                className="bg-default-100 text-default-700 hover:bg-default-200 inline-flex items-center gap-1 rounded-full border border-transparent px-2.5 py-1 text-xs font-medium transition-colors"
              >
                <span>{i18n._(item.label)}</span>
                {length !== null && (
                  <span className="text-default-500 text-[11px]">
                    ({length})
                  </span>
                )}
              </button>
            );
          }),
      )}
    </div>
  );
}
